import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -100]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?featured=true`);
        setFeaturedProducts(response.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO title="Premium Tank Tops" description="3T creates premium tank tops with bold, motivational messages. Born in Australia, built for those who move with purpose. Free shipping on 3+ items." />
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen overflow-hidden" data-testid="hero-section">
        {/* Parallax Background */}
        <motion.div
          style={{ y: heroY }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-[#050505]/40 to-[#050505] z-10" />
          <img
            src="https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/mzta2xmq_3T_dual_model_ecommerce.png"
            alt="Two surfers wearing 3T tanks at sunset beach"
            className="w-full h-full object-cover object-[25%_center]"
            loading="eager"
            fetchPriority="high"
          />
        </motion.div>

        {/* Hero Content */}
        <motion.div
          style={{ y: textY, opacity: heroOpacity }}
          className="relative z-20 h-full flex flex-col justify-end pb-24 px-6 md:px-12"
        >
          <div className="max-w-[1400px] mx-auto w-full">
            <motion.h1
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="font-heading text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]"
            >
              <span className="font-light" style={{ fontWeight: 300, textTransform: 'none' }}>Sometimes we all need a</span><br />
              <span className="text-[#FFD200] uppercase">REMINDER.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-8 text-lg md:text-xl text-white/90 max-w-lg font-medium"
            >
              We don't just make clothing.<br />
              We create reminders for everyday life.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-8"
            >
              <Link
                to="/shop"
                className="btn-primary inline-flex items-center gap-3"
                data-testid="hero-shop-btn"
              >
                CHOOSE YOUR REMINDER
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Marquee Band */}
      <section className="py-8 border-y border-white/10 bg-[#0A0A0A]" data-testid="marquee-section">
        <Marquee speed={50} gradient={false}>
          <span className="marquee-text mx-8">ILLUMINATE THE WORLD</span>
          <span className="marquee-text mx-8 text-[#FFD700]">•</span>
          <span className="marquee-text mx-8">INSPIRE</span>
          <span className="marquee-text mx-8 text-[#FFD700]">•</span>
          <span className="marquee-text mx-8">MOTIVATE</span>
          <span className="marquee-text mx-8 text-[#FFD700]">•</span>
          <span className="marquee-text mx-8">CULTIVATE</span>
          <span className="marquee-text mx-8 text-[#FFD700]">•</span>
          <span className="marquee-text mx-8">3T</span>
          <span className="marquee-text mx-8 text-[#FFD700]">•</span>
        </Marquee>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-6 md:px-12" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">
                NEW ARRIVALS
              </p>
              <h2 className="font-heading text-4xl md:text-6xl tracking-tight uppercase">
                THE REMINDER COLLECTION
              </h2>
              <p className="text-[#A3A3A3] text-lg mt-3">
                Premium apparel carrying ideas worth remembering.
              </p>
            </div>
            <Link
              to="/shop"
              className="link-underline font-mono text-sm tracking-wider hidden md:block"
              data-testid="view-all-link"
            >
              VIEW ALL
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="loader" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 product-grid">
              {featuredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}

          <Link
            to="/shop"
            className="btn-outline mt-16 mb-4 w-full md:hidden text-center block"
          >
            View All Products
          </Link>
        </div>
      </section>

      {/* Lifestyle Section with Parallax */}
      <section className="relative py-32 overflow-hidden" data-testid="lifestyle-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/h0fxp8nu_3T_surf_female_highres.png"
            alt="Woman surfer wearing 3T tank at sunset beach"
            className="w-full h-full object-cover opacity-30"
            loading="lazy"
          />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-4">
                OUR PHILOSOPHY
              </p>
              <h2 className="font-heading text-4xl md:text-6xl tracking-tight uppercase mb-6">
                EVERY SHIRT<br />
                IS A <span className="text-[#FFD700]">REMINDER</span>
              </h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed max-w-lg">
                Not a solution.<br />
                Just a reminder.
              </p>
              <Link
                to="/shop"
                className="btn-primary inline-flex items-center gap-3 mt-8"
                data-testid="discover-btn"
              >
                Discover The Idea
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="md:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="absolute -inset-4 border border-[#FFD700] opacity-50" />
                <img
                  src="https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/ph4l06aq_3T%20GRATITUDE2.png"
                  alt="Woman meditating with 3T tank on mountain top"
                  className="w-full aspect-[4/5] object-cover"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Grid Section */}
      <section className="py-24 px-6 md:px-12 bg-[#0A0A0A]" data-testid="social-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-12">
            <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-4">
              #3TLIFE
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight uppercase mb-3 text-white">
              One idea.
            </h2>
            <p className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight">
              Can change everything.
            </p>
            <p className="text-lg md:text-xl lg:text-2xl tracking-tight mt-6" style={{ textTransform: 'none' }}>
              Let's spread the <span className="text-[#FFD700] font-bold">WORD</span>,
            </p>
            <p className="text-lg md:text-xl lg:text-2xl tracking-tight" style={{ textTransform: 'none' }}>
              let's change the <span className="text-[#FFD700] font-bold">WORLD</span>
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              "https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/s1sl0sq3_ChatGPT%20Image%20May%205%2C%202026%2C%2012_08_52%20PM.png",
              "https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/5qw69dv5_ChatGPT%20Image%20May%205%2C%202026%2C%2012_09_02%20PM.png",
              "https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/yxhiawef_ChatGPT%20Image%20May%205%2C%202026%2C%2012_09_43%20PM.png"
            ].map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="aspect-square overflow-hidden group"
              >
                <img 
                  src={img} 
                  alt={`3T lifestyle ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 px-6 md:px-12" data-testid="newsletter-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-2xl">
            <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-4">
              STAY CONNECTED
            </p>
            <h2 className="font-heading text-4xl md:text-6xl tracking-tight uppercase mb-6">
              JOIN THE<br />3T COMMUNITY
            </h2>
            <p className="text-[#A3A3A3] text-lg mb-8">
              Join a community that believes clothing can carry more than style.
            </p>
            <form className="flex flex-col sm:flex-row gap-4" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email"
                className="input-underline flex-1"
                data-testid="newsletter-email"
              />
              <button type="submit" className="btn-primary whitespace-nowrap" data-testid="newsletter-submit">
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
